import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { WalkMeSDK } from 'capacitor-plugin-walkme-sdk';
import { environment } from 'src/environments/environment';

/**
 * Extends the global Window interface with WalkMe-specific properties
 * set during web SDK initialization and runtime language changes.
 */
declare global {
  interface Window {
    _walkmeConfig?: {
      smartLoad: boolean;
      userId?: string;
      customData?: Record<string, unknown>;
      language?: string;
    };
    /** Set to true after the WalkMe script has been injected to prevent duplicate loads. */
    _walkmeLoaded?: boolean;
    WalkMeAPI?: {
      changeLanguage: (language: string) => void;
    };
    langCode?: string;
    walkme_get_language?: () => string;
  }
}

/**
 * Manages WalkMe SDK lifecycle and user identification across platforms.
 *
 * On iOS/Android, delegates to the `capacitor-plugin-walkme-sdk` native plugin.
 * On web, injects the WalkMe script tag and sets `window._walkmeConfig` before load.
 */
export interface WalkMeUserData {
  operator?: {
    operator_id?: string;
    display_name?: string;
    manager_access_jwt?: string;
    permissions?: unknown;
  };
  device?: { facility_id?: number; tenant?: string };
  facilityName?: string;
  timeZone?: string;
  locationName?: string;
  locationType?: string;
  language?: string;
}

@Injectable({ providedIn: 'root' })
export class WalkMeService {
  private language: string | undefined;

  constructor() {}

  /**
   * Starts the WalkMe native SDK for iOS and Android.
   * Uses platform-specific credentials from the environment config.
   * No-op on web — web initialization is handled in `initializeUser`.
   */
  async startMobileSDK(): Promise<void> {
    const platform = Capacitor.getPlatform();

    if (platform !== 'ios' && platform !== 'android') {
      return;
    }

    try {
      if (platform === 'ios' && environment.walkme?.ios) {
        await WalkMeSDK.start(environment.walkme.ios);
      } else if (platform === 'android' && environment.walkme?.android) {
        await WalkMeSDK.start(environment.walkme.android);
      }
    } catch (error) {
      console.error('Error starting WalkMe SDK:', error);
    }
  }

  /**
   * Updates the active language in WalkMe.
   *
   * On mobile, sets the `language` private user attribute via the native SDK.
   * On web, updates `window._walkmeConfig.customData.language` if the config is already loaded.
   *
   * @param language - BCP 47 language tag (e.g. `'en'`, `'fr'`).
   */
  async changeLanguage(language: string): Promise<void> {
    this.language = language;

    const platform = Capacitor.getPlatform();

    if (platform === 'ios' || platform === 'android') {
      try {
        await WalkMeSDK.setPrivateUserAttribute({
          key: 'language',
          value: language
        });
      } catch (error) {
        console.error('Error setting WalkMe language:', error);
      }
    } else {
      if (window._walkmeConfig?.customData) {
        window._walkmeConfig.customData.language = language;
      }
    }
  }

  /**
   * Identifies the current operator in WalkMe and initializes the SDK if needed.
   *
   * Reads operator, device, facility, and location from the NGXS store and
   * passes them as custom attributes so WalkMe can personalize guidance.
   *
   * On mobile: sets the user ID and private attributes via the native SDK.
   * On web: injects the WalkMe script tag once (guarded by `window._walkmeLoaded`)
   * with `window._walkmeConfig` pre-populated. Requires `environment.walkme.webToken`.
   *
   * @param language - Optional BCP 47 language tag. Falls back to the last set language or `'en'`.
   */
  async initializeUser(data: WalkMeUserData): Promise<void> {
    if (data.language) {
      this.language = data.language;
    }

    const customData: Record<string, unknown> = {
      name: data.operator?.display_name,
      operator_id: data.operator?.operator_id,
      facility_id: `${data.device?.facility_id}`,
      facility_name: data.facilityName,
      location_name: data.locationName,
      location_type: data.locationType,
      language: this.language || 'en',
      tenant: data.device?.tenant,
      timezone: data.timeZone,
      has_manager_access: !!data.operator?.manager_access_jwt,
      permissions: data.operator?.permissions
    };

    const platform = Capacitor.getPlatform();

    if (platform === 'ios' || platform === 'android') {
      try {
        await WalkMeSDK.setUserID({ userID: data.operator?.operator_id });
        await WalkMeSDK.setPrivateUserAttributes({ attributes: customData });
      } catch (error) {
        console.error('Error setting WalkMe user ID:', error);
      }
    } else {
      if (!environment.walkme?.webToken || window._walkmeLoaded) {
        return;
      }

      window._walkmeConfig = {
        smartLoad: true,
        userId: data.operator?.operator_id,
        customData
      };

      const isProduction = environment.MSPOS_ENVIRONMENT === 'production';
      const walkmeEnvPath = isProduction ? '' : 'devtest/';
      const token = environment.walkme.webToken;

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `https://cdn.walkme.com/users/${token}/${walkmeEnvPath}walkme_${token}_https.js`;
      document.head.appendChild(script);

      window._walkmeLoaded = true;
    }
  }
}
